import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (redirectPath = null) => {
    // Save the intended redirect path for after login
    if (redirectPath) {
      localStorage.setItem('auth_redirect', redirectPath);
    } else {
      // Save current path if not on home
      const currentPath = window.location.pathname;
      if (currentPath !== '/' && currentPath !== '/auth/callback') {
        localStorage.setItem('auth_redirect', currentPath);
      }
    }
    
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
    setUser(null);
    window.location.href = '/';
  };

  const processSessionId = async (sessionId) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      });
      
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        return { success: true, user: data.user };
      }
      // Distinguish invalid credentials (401) from server errors (5xx)
      if (response.status >= 500) {
        return { success: false, error: 'server', message: 'Server is not responding. Please try again in a moment.' };
      }
      if (response.status === 401) {
        return { success: false, error: 'invalid', message: 'Your sign-in session is invalid or has expired. Please sign in again.' };
      }
      return { success: false, error: 'unknown', message: `Sign-in failed (status ${response.status}).` };
    } catch (error) {
      console.error('Session processing failed:', error);
      return { success: false, error: 'network', message: 'Network error. Please check your connection and try again.' };
    }
  };

  const isAdmin = user?.role === 'admin';

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      processSessionId,
      checkAuth,
      isAdmin
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
